import { md5, sha1 } from "@noble/hashes/legacy.js";
import { sha256 } from "@noble/hashes/sha2.js";
import type JSZip from "jszip";
import type { ArchiveEntry, JarIntegrityAssessment } from "./types";

interface ManifestSection {
  attributes: Map<string, string>;
}

function parseSections(raw: string) {
  const unfolded: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith(" ") && unfolded.length) {
      unfolded[unfolded.length - 1] += line.slice(1);
    } else {
      unfolded.push(line);
    }
  }
  const sections: ManifestSection[] = [];
  let current = new Map<string, string>();
  const flush = () => {
    if (current.size) sections.push({ attributes: current });
    current = new Map();
  };
  for (const line of unfolded) {
    if (!line.trim()) {
      flush();
      continue;
    }
    const separator = line.indexOf(":");
    if (separator < 1) continue;
    current.set(
      line.slice(0, separator).trim().toLowerCase(),
      line.slice(separator + 1).trim(),
    );
  }
  flush();
  return sections;
}

function base64(bytes: Uint8Array) {
  let binary = "";
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return globalThis.btoa(binary);
}

function digest(algorithm: string, bytes: Uint8Array) {
  if (algorithm === "sha-256") return base64(sha256(bytes));
  if (algorithm === "sha1" || algorithm === "sha") return base64(sha1(bytes));
  if (algorithm === "md5") return base64(md5(bytes));
  return null;
}

function findDigest(attributes: Map<string, string>, suffix = "-digest") {
  for (const [key, value] of attributes) {
    if (!key.endsWith(suffix)) continue;
    const algorithm = key.slice(0, -suffix.length);
    if (["sha-256", "sha1", "sha", "md5"].includes(algorithm)) {
      return { algorithm, expected: value };
    }
  }
  return null;
}

export async function assessJarIntegrity(
  archive: JSZip,
  entries: ArchiveEntry[],
  signatureFiles: string[],
): Promise<JarIntegrityAssessment> {
  const manifestEntry =
    archive.file("META-INF/MANIFEST.MF") ??
    Object.values(archive.files).find(
      (entry) => entry.name.toLowerCase() === "meta-inf/manifest.mf",
    );
  const sfFiles = signatureFiles.filter((path) => /\.sf$/i.test(path));
  const signerFiles = signatureFiles.filter((path) =>
    /\.(?:rsa|dsa|ec)$/i.test(path),
  );
  const result: JarIntegrityAssessment = {
    status: "unsigned",
    signedEntryCount: 0,
    verifiedEntryCount: 0,
    invalidEntries: [],
    unsignedEntries: [],
    missingEntries: [],
    signerFiles,
    signatureFiles,
    weakAlgorithms: [],
    coveragePercent: 0,
    notes: [],
  };

  if (!manifestEntry || (!sfFiles.length && !signerFiles.length)) {
    result.unsignedEntries = entries
      .filter(
        (entry) =>
          !entry.directory &&
          !/^meta-inf\/(?:manifest\.mf|[^/]+\.(?:sf|rsa|dsa|ec))$/i.test(
            entry.path,
          ),
      )
      .map((entry) => entry.path)
      .slice(0, 500);
    result.notes.push("No JAR signature metadata was found.");
    return result;
  }

  const manifestBytes = await manifestEntry.async("uint8array");
  const manifestRaw = new TextDecoder("utf-8", { fatal: false }).decode(
    manifestBytes,
  );
  const sections = parseSections(manifestRaw);
  const signedNames = new Set<string>();

  for (const section of sections.slice(1)) {
    const path = section.attributes.get("name");
    const signedDigest = findDigest(section.attributes);
    if (!path || !signedDigest) continue;
    result.signedEntryCount += 1;
    signedNames.add(path);
    if (signedDigest.algorithm === "sha1" || signedDigest.algorithm === "sha") {
      result.weakAlgorithms.push("SHA-1");
    }
    if (signedDigest.algorithm === "md5") result.weakAlgorithms.push("MD5");
    const entry = archive.file(path);
    if (!entry) {
      result.missingEntries.push(path);
      continue;
    }
    const bytes = await entry.async("uint8array");
    const actual = digest(signedDigest.algorithm, bytes);
    if (!actual || actual !== signedDigest.expected) {
      result.invalidEntries.push({
        path,
        algorithm: signedDigest.algorithm.toUpperCase(),
        expected: signedDigest.expected,
        actual: actual ?? "unsupported",
      });
    } else {
      result.verifiedEntryCount += 1;
    }
  }

  const ordinaryEntries = entries.filter(
    (entry) =>
      !entry.directory &&
      !/^meta-inf\/(?:manifest\.mf|[^/]+\.(?:sf|rsa|dsa|ec))$/i.test(
        entry.path,
      ),
  );
  result.unsignedEntries = ordinaryEntries
    .filter((entry) => !signedNames.has(entry.path))
    .map((entry) => entry.path)
    .slice(0, 500);
  result.coveragePercent = ordinaryEntries.length
    ? Math.round((signedNames.size / ordinaryEntries.length) * 1000) / 10
    : 0;
  result.weakAlgorithms = unique(result.weakAlgorithms);

  for (const sfPath of sfFiles.slice(0, 8)) {
    const sfEntry = archive.file(sfPath);
    if (!sfEntry) continue;
    const sfRaw = await sfEntry.async("text");
    const main = parseSections(sfRaw)[0]?.attributes;
    if (!main) continue;
    const manifestDigest = findDigest(main, "-digest-manifest");
    if (!manifestDigest) continue;
    const actual = digest(manifestDigest.algorithm, manifestBytes);
    result.manifestDigestValid = actual === manifestDigest.expected;
    if (!result.manifestDigestValid) {
      result.invalidEntries.push({
        path: "META-INF/MANIFEST.MF",
        algorithm: manifestDigest.algorithm.toUpperCase(),
        expected: manifestDigest.expected,
        actual: actual ?? "unsupported",
      });
    }
    if (
      manifestDigest.algorithm === "sha1" ||
      manifestDigest.algorithm === "sha"
    ) {
      result.weakAlgorithms.push("SHA-1");
    }
  }
  result.weakAlgorithms = unique(result.weakAlgorithms);

  if (result.invalidEntries.length || result.missingEntries.length) {
    result.status = "tampered";
    result.notes.push(
      "One or more signed entry digests do not match the current archive.",
    );
  } else if (!sfFiles.length || !signerFiles.length) {
    result.status = "incomplete";
    result.notes.push(
      "The archive has partial signature metadata but no complete .SF and cryptographic block pair.",
    );
  } else if (result.verifiedEntryCount > 0) {
    result.status = "entry-digests-valid";
    result.notes.push(
      "Signed entry digests match. JarLab does not authenticate the PKCS#7 signer certificate chain in-browser.",
    );
  } else {
    result.status = "unverifiable";
    result.notes.push(
      "Signature files exist, but no supported per-entry digest could be verified.",
    );
  }
  if (result.unsignedEntries.length) {
    result.notes.push(
      `${result.unsignedEntries.length.toLocaleString()} ordinary entries are not covered by manifest digests.`,
    );
  }
  if (result.weakAlgorithms.length) {
    result.notes.push(
      `Legacy digest algorithms present: ${result.weakAlgorithms.join(", ")}.`,
    );
  }
  return result;
}

function unique(values: string[]) {
  return Array.from(new Set(values));
}

